import { AlertCircle, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";

import { AUTH_PATHS, WAITLIST_PATH } from "../../../constants";
import { FeatureFlag } from "../../../constants/featureFlags";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag";
import { PasswordInput, TextInput } from "../../shared/FormInputs";
import { Loading } from "../../ui";

interface SignInViewProps {
  email: string;
  password: string;
  mode: "signin" | "forgotPassword";
  isLoading: boolean;
  error?: string;
  sessionError?: string;
  setError: (error: string) => void;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onModeChange: (mode: "signin" | "forgotPassword") => void;
  onSubmit: (e: React.FormEvent) => void;
}

export const SignInView = ({
  email,
  password,
  mode,
  isLoading,
  error,
  sessionError,
  setError,
  onEmailChange,
  onPasswordChange,
  onModeChange,
  onSubmit,
}: SignInViewProps) => {
  const signupEnabled = useFeatureFlag(FeatureFlag.SignupEnabled);
  const handleModeChange = (mode: "signin" | "forgotPassword") => {
    setError("");
    onEmailChange("");
    onPasswordChange("");
    onModeChange(mode);
  };
  return (
    <div>
      <div className="mb-8">
        <h2 className="mb-2 text-3xl font-bold text-foreground">
          {mode === "signin" ? "Sign In" : "Reset Password"}
        </h2>
        {sessionError && (
          <div className="mb-6 flex items-center gap-3 rounded-2xl border border-warning-border bg-warning-subtle/50 p-4 text-sm text-warning-text shadow-sm shadow-warning-border/20 duration-500 animate-in fade-in slide-in-from-top-2">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-warning-subtle-strong/80">
              <AlertTriangle className="h-6 w-6 text-warning-text" />
            </div>
            <p className="font-medium leading-relaxed">{sessionError}</p>
          </div>
        )}
        <p className="text-muted-foreground">
          {mode === "signin"
            ? "Welcome back! Please enter your details."
            : "Enter your email to receive a reset link."}
        </p>
      </div>

      <form onSubmit={onSubmit}>
        <TextInput
          type="email"
          label="Email Address"
          value={email}
          placeholder="Enter your email"
          onChange={onEmailChange}
          autoComplete="email"
          required
        />
        {mode === "signin" && (
          <PasswordInput
            label="Password"
            value={password}
            placeholder="Enter your password"
            onChange={onPasswordChange}
            autoComplete="current-password"
            required
          />
        )}

        {error && (
          <div className="mb-6 rounded-r-lg border-l-4 border-destructive bg-destructive-subtle p-4 text-sm text-destructive-text">
            <div className="flex items-center">
              <AlertCircle className="mr-2 h-5 w-5" />
              {error}
            </div>
          </div>
        )}

        <div className="space-y-6">
          <div className="flex items-center justify-end">
            {mode === "forgotPassword" ? (
              <button
                type="button"
                className="text-sm font-semibold text-primary transition-colors hover:text-primary-hover"
                onClick={(e) => {
                  e.preventDefault();
                  handleModeChange("signin");
                }}
              >
                Go back to Sign In
              </button>
            ) : (
              <button
                type="button"
                className="text-sm font-semibold text-primary transition-colors hover:text-primary-hover"
                onClick={(e) => {
                  e.preventDefault();
                  handleModeChange("forgotPassword");
                }}
              >
                Forgot password?
              </button>
            )}
          </div>

          <button
            className={`w-full rounded-xl bg-primary px-4 py-3.5 font-bold text-white shadow-sm transition-all duration-200 hover:bg-primary-hover focus-visible:shadow-focus ${
              isLoading ? "transform-none cursor-not-allowed opacity-70" : "hover:-translate-y-0.5"
            }`}
            type="submit"
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="flex items-center justify-center">
                <span className="mr-3">
                  <Loading size={20} tone="inverse" fullScreen={false} />
                </span>
                Processing...
              </span>
            ) : mode === "forgotPassword" ? (
              "Send Reset Email"
            ) : (
              "Sign In"
            )}
          </button>
        </div>
      </form>

      {mode === "signin" && (
        <p className="mt-8 text-center text-sm text-muted-foreground">
          {!signupEnabled ? (
            <>
              Want early access?{" "}
              <Link
                to={WAITLIST_PATH}
                className="font-semibold text-primary transition-colors hover:text-primary-hover"
              >
                Join the waitlist
              </Link>
            </>
          ) : (
            <>
              Don&apos;t have an account?{" "}
              <Link
                to={AUTH_PATHS.SIGNUP}
                className="font-semibold text-primary transition-colors hover:text-primary-hover"
              >
                Sign up
              </Link>
            </>
          )}
        </p>
      )}
    </div>
  );
};
